/**
 * OKLCH — the colour maths the palette engine is built on.
 *
 * WHY THIS FILE EXISTS. A palette used to be a hand-written CSS block: twelve
 * `oklch(L C H)` literals per hue, with the chroma of each stop chosen by eye
 * and checked by eye. That does not scale past the four themes in the tree,
 * and it silently shipped values that were WRONG — `--accent-8` was
 * `oklch(0.520 0.110 195)` while cyan's sRGB ceiling at that lightness is
 * ~0.094, so every browser gamut-mapped it down to a duller colour than the
 * file claimed. The comment said "electric"; the screen said dusty, and
 * nothing in the pipeline could tell the difference.
 *
 * So the ramp stops being authored and starts being DERIVED. Give this module
 * a hue and it returns twelve stops that are in gamut by construction. A
 * palette becomes two numbers plus a name — small enough to live in a database
 * row and be swapped at runtime from the admin surface.
 *
 * THE ONE THING TO UNDERSTAND ABOUT OKLCH: the chroma ceiling is a function of
 * BOTH lightness and hue, and the shape of that function differs wildly per
 * hue. Blues carry their chroma high (cerulean peaks at L 0.75) and violets
 * carry it low (violet peaks near L 0.55). A single lightness ladder applied to
 * every hue therefore flatters some and starves others — which is exactly why
 * `rampFor` locates each hue's own peak rather than assuming one.
 *
 * No dependencies, no colour library: the conversion is thirty lines and a
 * library would be a version to keep and a supply-chain edge for no gain.
 */

/** A colour in OKLCH. Hue is degrees, 0–360. */
export interface Oklch {
  l: number;
  c: number;
  h: number;
}

/** Linear-light sRGB, unclamped — negative or >1 means out of gamut. */
interface LinearRgb {
  r: number;
  g: number;
  b: number;
}

/**
 * OKLab → linear sRGB, via the LMS cone space. Coefficients are Björn
 * Ottosson's published matrices; they are the same ones every browser uses, so
 * what this computes is what Chrome paints.
 */
function toLinear({ l, c, h }: Oklch): LinearRgb {
  const rad = (h * Math.PI) / 180;
  const a = c * Math.cos(rad);
  const b = c * Math.sin(rad);

  const l_ = l + 0.3963377774 * a + 0.2158037573 * b;
  const m_ = l - 0.1055613458 * a - 0.0638541728 * b;
  const s_ = l - 0.0894841775 * a - 1.291485548 * b;

  const L = l_ * l_ * l_;
  const M = m_ * m_ * m_;
  const S = s_ * s_ * s_;

  return {
    r: 4.0767416621 * L - 3.3077115913 * M + 0.2309699292 * S,
    g: -1.2684380046 * L + 2.6097574011 * M - 0.3413193965 * S,
    b: -0.0041960863 * L - 0.7034186147 * M + 1.707614701 * S,
  };
}

/** The sRGB transfer function. */
function encode(v: number): number {
  const x = Math.max(0, Math.min(1, v));
  return x <= 0.0031308 ? 12.92 * x : 1.055 * Math.pow(x, 1 / 2.4) - 0.055;
}

/**
 * Is this colour representable in sRGB?
 *
 * The epsilon is deliberate. Floating-point round-trips land a hair outside
 * the cube for colours that are mathematically on its surface, and treating
 * those as out-of-gamut makes the binary search below converge one step short
 * of the true boundary — visible as a ramp that is uniformly a touch flat.
 */
export function outOfGamut(colour: Oklch, epsilon = 0.0015): boolean {
  const { r, g, b } = toLinear(colour);
  return [r, g, b].some((v) => v < -epsilon || v > 1 + epsilon);
}

/** OKLCH → `#rrggbb`, clamped into sRGB. For swatches and canvases only — the app itself emits `oklch()` and lets the browser handle wide gamut. */
export function toHex(colour: Oklch): string {
  const { r, g, b } = toLinear(colour);
  const hex = [r, g, b]
    .map((v) => Math.round(encode(v) * 255).toString(16).padStart(2, "0"))
    .join("");
  return `#${hex}`;
}

/** OKLCH → the `oklch(L C H)` string a stylesheet consumes. */
export function toCss({ l, c, h }: Oklch): string {
  return `oklch(${l.toFixed(3)} ${c.toFixed(3)} ${h.toFixed(1)})`;
}

/**
 * The largest chroma that stays inside sRGB at this lightness and hue.
 *
 * Binary search rather than a closed form: the gamut boundary in OKLCH has no
 * cheap analytic expression, and 32 iterations converges far below one 8-bit
 * step. `hi` starts at 0.45 because no sRGB colour exceeds ~0.37 chroma.
 */
export function maxChroma(l: number, h: number): number {
  let lo = 0;
  let hi = 0.45;
  for (let i = 0; i < 32; i += 1) {
    const mid = (lo + hi) / 2;
    if (outOfGamut({ l, c: mid, h })) hi = mid;
    else lo = mid;
  }
  return lo;
}

/**
 * Where this hue is at its most saturated.
 *
 * This is the number that decides whether an accent reads as electric or as
 * dust, and it moves by 0.2 of lightness across the wheel. Sampling beats
 * calculus here: the curve is smooth and single-peaked, and 0.01 steps resolve
 * it far finer than a ramp stop can use.
 */
export function chromaPeak(h: number): { l: number; c: number } {
  let best = { l: 0.5, c: 0 };
  for (let l = 0.3; l <= 0.95; l += 0.01) {
    const c = maxChroma(l, h);
    if (c > best.c) best = { l, c };
  }
  return best;
}

/** WCAG relative luminance of an OKLCH colour. */
export function relativeLuminance(colour: Oklch): number {
  const { r, g, b } = toLinear(colour);
  const lin = (v: number) => {
    const s = encode(v);
    return s <= 0.04045 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

/** WCAG contrast ratio, 1–21. */
export function contrastRatio(a: Oklch, b: Oklch): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

export const WHITE: Oklch = { l: 1, c: 0, h: 0 };

/**
 * THE LIGHTNESS LADDER — twelve rungs, identical for every hue.
 *
 * Lightness is fixed and chroma is derived, never the reverse. That is what
 * makes two palettes interchangeable: stop 3 is the same *brightness* of wash
 * in cerulean as in violet, so a component that picked `accent-3` for a
 * hover fill keeps working when the palette changes underneath it. If the
 * ladder moved per hue, every swap would silently re-weight the whole UI.
 */
export const LADDER = [
  0.985, 0.965, 0.935, 0.895, 0.845, 0.75, 0.66, 0.57, 0.5, 0.41, 0.31, 0.225,
] as const;

/** How close to the gamut boundary a stop sits. Backing off a touch avoids the visible banding that exact-boundary colours show on 8-bit panels. */
const GAMUT_MARGIN = 0.98;

/** Chroma is capped here regardless of hue, so no palette can be so loud that large fills vibrate. */
const CHROMA_CAP = 0.3;

/**
 * The wash stops (1–3) are held well under their ceiling on purpose. They are
 * used as large fills — selection, hover, soft status backgrounds — and a
 * large fill at full chroma tints the whole page. The accent should live on a
 * small fraction of the pixels.
 */
const WASH_SCALE = [0.35, 0.45, 0.55] as const;

export interface Ramp {
  /** Twelve chroma values, index-aligned to `LADDER`. */
  chroma: number[];
  /** Index of the stop the dark face uses as `--primary` — the hue's own peak. */
  darkPrimary: number;
  /** Index of the stop the light face uses as `--primary` — lightest that clears 4.5:1 with white. */
  lightPrimary: number;
}

/**
 * Build a gamut-safe ramp for one hue.
 *
 * Both primaries are CHOSEN, not assumed:
 *
 *   dark  — the stop nearest this hue's chroma peak, because on a deep ground
 *           the accent's job is to glow and the peak is where it can.
 *
 *   light — the LIGHTEST stop that still clears 4.5:1 against white text.
 *           Picking by contrast rather than by index is what lets a palette be
 *           authored by a non-designer from the admin surface without shipping
 *           an unreadable button; the search runs light-to-dark and takes the
 *           first pass, so the accent stays as vivid as legibility allows.
 */
export function rampFor(hue: number): Ramp {
  const peak = chromaPeak(hue);

  const chroma = LADDER.map((l, i) => {
    const ceiling = Math.min(maxChroma(l, hue) * GAMUT_MARGIN, CHROMA_CAP);
    return i < WASH_SCALE.length ? ceiling * WASH_SCALE[i] : ceiling;
  });

  let darkPrimary = 0;
  for (let i = 1; i < LADDER.length; i += 1) {
    if (Math.abs(LADDER[i] - peak.l) < Math.abs(LADDER[darkPrimary] - peak.l)) {
      darkPrimary = i;
    }
  }

  // Start at 4: stops 1–3 are washes and can never carry white text.
  let lightPrimary = LADDER.length - 1;
  for (let i = 3; i < LADDER.length; i += 1) {
    if (contrastRatio({ l: LADDER[i], c: chroma[i], h: hue }, WHITE) >= 4.5) {
      lightPrimary = i;
      break;
    }
  }

  return { chroma, darkPrimary, lightPrimary };
}

/** The stops of a ramp as OKLCH colours, for previewing and testing. */
export function rampColours(hue: number, ramp: Ramp = rampFor(hue)): Oklch[] {
  return LADDER.map((l, i) => ({ l, c: ramp.chroma[i], h: hue }));
}
